import React, { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, ReferenceArea,
} from 'recharts';
import { cn } from 'lib/utils';

const COLORS = { web_app: '#254A73', storefront: '#4CAF50' };

export default function OverviewTab({ data, loading, gameweeks }) {
  // Compute summary cards from the data with period-over-period comparison
  const summary = useMemo(() => {
    if (!data || data.length === 0) return null;

    const totalUsers = data.reduce((sum, d) => sum + d.unique_users, 0);
    const totalSessions = data.reduce((sum, d) => sum + d.sessions, 0);
    const totalPageviews = data.reduce((sum, d) => sum + d.pageviews, 0);
    const avgEngagedRate = data.length > 0
      ? data.reduce((sum, d) => sum + d.engaged_session_rate, 0) / data.length
      : 0;
    const totalNewUsers = data.reduce((sum, d) => sum + d.new_users, 0);
    const totalSignups = data.reduce((sum, d) => sum + d.signups, 0);

    // Split into first half / second half for % change
    const sorted = [...data].sort((a, b) => a.date.localeCompare(b.date));
    const mid = Math.floor(sorted.length / 2);
    const first = sorted.slice(0, mid);
    const second = sorted.slice(mid);
    const pctChange = (secondHalf, firstHalf, key) => {
      const s = secondHalf.reduce((sum, d) => sum + (d[key] || 0), 0);
      const f = firstHalf.reduce((sum, d) => sum + (d[key] || 0), 0);
      return f > 0 ? ((s - f) / f) * 100 : 0;
    };

    const changes = {
      users: pctChange(second, first, 'unique_users'),
      sessions: pctChange(second, first, 'sessions'),
      pageviews: pctChange(second, first, 'pageviews'),
      new_users: pctChange(second, first, 'new_users'),
      signups: pctChange(second, first, 'signups'),
    };

    return { totalUsers, totalSessions, totalPageviews, avgEngagedRate, totalNewUsers, totalSignups, changes };
  }, [data]);

  // Build daily time series (sum across properties for combined view)
  const dailyData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const byDate = {};
    data.forEach((row) => {
      if (!byDate[row.date]) {
        byDate[row.date] = { date: row.date, users: 0, sessions: 0, pageviews: 0, new_users: 0 };
      }
      byDate[row.date].users += row.unique_users;
      byDate[row.date].sessions += row.sessions;
      byDate[row.date].pageviews += row.pageviews;
      byDate[row.date].new_users += row.new_users;
    });

    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  // Build per-property daily data for split chart
  const propertyData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const byDate = {};
    data.forEach((row) => {
      if (!byDate[row.date]) {
        byDate[row.date] = { date: row.date, web_app: 0, storefront: 0 };
      }
      byDate[row.date][row.property] += row.unique_users;
    });

    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  // Compute gameweek shaded bands that overlap the chart date range
  const gwBands = useMemo(() => {
    if (!gameweeks || !dailyData || dailyData.length === 0) return [];
    const chartDates = dailyData.map((d) => d.date);
    const minDate = chartDates[0];
    const maxDate = chartDates[chartDates.length - 1];

    const bands = [];
    gameweeks.forEach((gw) => {
      const toDateStr = (iso) => iso ? iso.slice(0, 10) : null;
      const x1 = toDateStr(gw.deadline_time);
      const x2 = toDateStr(gw.last_kickoff) || toDateStr(gw.first_kickoff) || x1;
      if (!x1 || !x2) return;
      // Check overlap with chart range
      if (x2 < minDate || x1 > maxDate) return;
      bands.push({ id: gw.id, x1: x1 < minDate ? minDate : x1, x2: x2 > maxDate ? maxDate : x2 });
    });
    return bands;
  }, [gameweeks, dailyData]);

  if (!data || data.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-token-md text-muted-foreground">No data available. Run the pipeline first.</p>
      </div>
    );
  }

  const cards = [
    { label: 'Total Users', value: summary?.totalUsers?.toLocaleString(), color: '#254A73', change: summary?.changes?.users },
    { label: 'Sessions', value: summary?.totalSessions?.toLocaleString(), color: '#2196f3', change: summary?.changes?.sessions },
    { label: 'Pageviews', value: summary?.totalPageviews?.toLocaleString(), color: '#9c27b0', change: summary?.changes?.pageviews },
    { label: 'Engaged Rate', value: `${(summary?.avgEngagedRate * 100).toFixed(1)}%`, color: '#4caf50' },
    { label: 'New Users', value: summary?.totalNewUsers?.toLocaleString(), color: '#ff9800', change: summary?.changes?.new_users },
    { label: 'Signups', value: summary?.totalSignups?.toLocaleString(), color: '#f44336', change: summary?.changes?.signups },
  ];

  const chartCard = (title, chart) => (
    <div className="rounded-token-large border border-borderGrey bg-white p-4">
      <h3 className="mb-2 text-token-xl font-semibold">{title}</h3>
      {chart}
    </div>
  );

  return (
    <div className="w-full min-w-0">
      {/* Summary Cards */}
      <div className="mb-1 grid grid-cols-3 gap-3 xl:grid-cols-6">
        {cards.map((card) => (
          <div
            key={card.label}
            className="rounded-token-large border border-borderGrey bg-white p-3 text-center"
            style={{ borderLeft: `4px solid ${card.color}` }}
          >
            <p className="text-token-sm text-muted-foreground">{card.label}</p>
            <p className="text-token-2xl font-semibold">{card.value}</p>
            {card.change != null && card.change !== 0 && (
              <p
                className={cn(
                  'text-token-xs font-semibold',
                  card.change > 0 ? 'text-success' : 'text-error'
                )}
                title="Second half vs first half of the selected date range"
              >
                {card.change > 0 ? '▲' : '▼'} {Math.abs(card.change).toFixed(1)}%
              </p>
            )}
          </div>
        ))}
      </div>
      <p className="mb-4 text-token-xs text-muted-foreground">
        Δ compares the second half vs the first half of the selected date range.
      </p>

      {/* Charts — 2-col grid on desktop so all three fit above the fold */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="xl:col-span-2">
          {chartCard(
            'Daily Users & Sessions',
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyData}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#DADADA" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="users" stroke="#254A73" strokeWidth={2} dot={false} name="Users" isAnimationActive={false} />
                <Line type="monotone" dataKey="sessions" stroke="#2196f3" strokeWidth={2} dot={false} name="Sessions" isAnimationActive={false} />
                {gwBands.map((band) => (
                  <ReferenceArea key={band.id} x1={band.x1} x2={band.x2} fill="#254A73" fillOpacity={0.06}
                    label={{ value: `GW${band.id}`, position: 'top', fontSize: 10, fill: '#254A73' }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {chartCard(
          'Users by Property',
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={propertyData}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#DADADA" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="web_app" fill={COLORS.web_app} name="Web App" stackId="a" />
              <Bar dataKey="storefront" fill={COLORS.storefront} name="Storefront" stackId="a" />
              {gwBands.map((band) => (
                <ReferenceArea key={band.id} x1={band.x1} x2={band.x2} fill="#254A73" fillOpacity={0.06}
                  label={{ value: `GW${band.id}`, position: 'top', fontSize: 10, fill: '#254A73' }} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}

        {chartCard(
          'New vs Returning Users',
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyData.map((d) => ({ ...d, returning: d.users - d.new_users }))}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#DADADA" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="new_users" fill="#ff9800" name="New Users" stackId="a" />
              <Bar dataKey="returning" fill="#254A73" name="Returning" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
